import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { LoggerService } from '@app/logger/logger.service';
import { StockEquipmentsService } from '@app/shared/services/entities/stockEquipments.service';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '@app/material.module';
const log = new LoggerService('EQUIPMENTS FORM COMPONENT');

// Enum para tipos de mantenimiento
export enum MaintenanceType {
  PREVENTIVO = 'preventivo',
  CORRECTIVO = 'correctivo'
}

interface MaintenanceEvent {
  date: Date;
  equipment: any;
  maintenanceData: any;
  type: 'preventivo' | 'correctivo';
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
  ],
  selector: 'app-maintenanceCalendar-form',
  templateUrl: './maintenanceCalendar-form.component.html',
  styleUrls: ['./maintenanceCalendar-form.component.scss']
})
export class MaintenanceCalendarFormComponent implements OnInit {

  public record = null;
  public currentDate: Date = new Date();
  public currentMonth: number;
  public currentYear: number;
  public calendarDays: any[] = [];
  public weekDays: string[] = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  public monthNames: string[] = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  public maintenanceEvents: Map<string, MaintenanceEvent[]> = new Map();
  public selectedDayEvents: MaintenanceEvent[] = [];
  public showDetailPanel: boolean = false;
  
  constructor(
    public dialogRef: MatDialogRef<MaintenanceCalendarFormComponent>,
    private customMessageService: CustomMessageService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private inventoryEquipmentsService: StockEquipmentsService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    this.record = this.data.record;
    this.currentMonth = this.currentDate.getMonth();
    this.currentYear = this.currentDate.getFullYear();
    
    this.processMaintenanceData();
    this.generateCalendar();
  }

  processMaintenanceData() {
    this.maintenanceEvents.clear();
    
    if (!this.record || !Array.isArray(this.record)) {
      return;
    }

    this.record.forEach(equipment => {
      // Procesar mantenimiento preventivo
      if (equipment.preventiveMaintenanceSchedule && Array.isArray(equipment.preventiveMaintenanceSchedule)) {
        equipment.preventiveMaintenanceSchedule.forEach((maintenance: any) => {
          if (maintenance.date) {
            const dateKey = this.getDateKey(new Date(maintenance.date));
            
            if (!this.maintenanceEvents.has(dateKey)) {
              this.maintenanceEvents.set(dateKey, []);
            }
            
            this.maintenanceEvents.get(dateKey)!.push({
              date: new Date(maintenance.date),
              equipment: equipment,
              maintenanceData: maintenance,
              type: 'preventivo'
            });
          }
        });
      }

      // Procesar mantenimiento correctivo
      if (equipment.correctiveMaintenance && Array.isArray(equipment.correctiveMaintenance)) {
        equipment.correctiveMaintenance.forEach((maintenance: any) => {
          if (maintenance.date) {
            const dateKey = this.getDateKey(new Date(maintenance.date));
            
            if (!this.maintenanceEvents.has(dateKey)) {
              this.maintenanceEvents.set(dateKey, []);
            }
            
            this.maintenanceEvents.get(dateKey)!.push({
              date: new Date(maintenance.date),
              equipment: equipment,
              maintenanceData: maintenance,
              type: 'correctivo'
            });
          }
        });
      }
    });
  }

  generateCalendar() {
    this.calendarDays = [];
    const firstDay = new Date(this.currentYear, this.currentMonth, 1);
    const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const totalDays = lastDay.getDate();

    // Días del mes anterior
    for (let i = 0; i < startingDayOfWeek; i++) {
      this.calendarDays.push({ day: null, isCurrentMonth: false });
    }

    // Días del mes actual
    for (let day = 1; day <= totalDays; day++) {
      const currentDate = new Date(this.currentYear, this.currentMonth, day);
      const dateKey = this.getDateKey(currentDate);
      const events = this.maintenanceEvents.get(dateKey) || [];
      const isToday = this.isToday(currentDate);

      this.calendarDays.push({
        day: day,
        isCurrentMonth: true,
        date: currentDate,
        events: events,
        hasEvents: events.length > 0,
        isToday: isToday
      });
    }
  }

  getDateKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }

  previousMonth() {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.generateCalendar();
  }

  nextMonth() {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.generateCalendar();
  }

  onDayClick(calendarDay: any) {
    if (calendarDay.hasEvents) {
      this.selectedDayEvents = calendarDay.events;
      this.showDetailPanel = true;
    }
  }

  closeDetailPanel() {
    this.showDetailPanel = false;
    this.selectedDayEvents = [];
  }

  getMaintenanceTypeClass(type: string): string {
    return type === 'preventivo' ? 'preventive' : 'corrective';
  }

  getMaintenanceTypeLabel(type: string): string {
    return type === 'preventivo' ? 'Preventivo' : 'Correctivo';
  }
}